import {
  Autocomplete,
  AutocompleteProps,
  Box,
  Button,
  Chip,
  Stack,
  TextField,
  createFilterOptions,
} from "@mui/material";
import { useCallback, useMemo, useState } from "react";
import { useEditCandidateProfileMutation } from "../../../../App/Queries/CandidateQueries";
import { useSearchTagsQuery } from "../../../../App/Queries/FilterQueries";
import { useNotification } from "../../../../App/State/Hooks/notificationHooks";
import { ICandidateProfile } from "../../../../App/Typings/CandidateProfile";

export interface ITagOption {
  inputValue?: string;
  label: string;
  isNew?: boolean;
}

const filter = createFilterOptions<ITagOption>();

export const TagAutoComplete = ({
  onSelect,
  existingTags,
  disabled,
  onDelete,
}: {
  onSelect: (tag: string[]) => void;
  existingTags: string[];
  disabled: boolean;
  onDelete: (tag: string, isNew: boolean) => void;
}) => {
  const { data, isLoading } = useSearchTagsQuery();

  const availableOptions = useMemo(
    () =>
      !isLoading && data
        ? data.filter((tag) => !existingTags.includes(tag)).map((tag) => ({ label: tag }))
        : [],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [data, isLoading, JSON.stringify(existingTags)],
  );

  const valueOptions = useMemo(
    () => existingTags.map((tag) => ({ label: tag })),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [JSON.stringify(existingTags)],
  );

  const handleChange = (_event: any, newValue: (string | ITagOption)[], reason: string) => {
    if (!newValue.length) {
      return;
    }
    if (reason === "selectOption" || reason === "createOption") {
      onSelect(
        newValue.map((option) => {
          if (typeof option === "string") {
            return availableOptions.find((opt) => opt.label === option)?.label ?? option;
          }
          if (option.inputValue && option.isNew) {
            return option.inputValue;
          }
          return option.label;
        }),
      );
    }
  };

  const filterOptions = (options: ITagOption[], params: any) => {
    const filtered = filter(options, params);
    const { inputValue } = params;

    const trimmedInput = inputValue.trim();
    if (trimmedInput !== "") {
      const existsInOptions = options.some(
        (option) => option.label === trimmedInput || option.inputValue === trimmedInput,
      );
      const existsInTags = existingTags.some((tag) => tag === trimmedInput);
      if (!existsInOptions && !existsInTags) {
        filtered.push({
          inputValue: trimmedInput,
          label: `Add "${trimmedInput}"`,
          isNew: true,
        });
      }
    }
    return filtered;
  };

  const getOptionLabel = (option: ITagOption | string) =>
    typeof option === "string" ? option : option.inputValue || option.label;

  const renderOption: AutocompleteProps<ITagOption, true, true, true, "div">["renderOption"] = (
    props,
    option,
  ) => {
    let { key, ...rest } = props;
    const optionKey = option.inputValue || option.label;
    const randomKey = Math.random().toString(36).substring(2, 15);
    key = `${optionKey.replace(/[^a-zA-Z0-9]/g, "")}-${randomKey}`;

    return (
      <Box component="li" key={key} {...rest}>
        {option.isNew ? `Add "${option.inputValue}"` : option.label}
      </Box>
    );
  };

  const renderTags: AutocompleteProps<ITagOption, true, true, true, "div">["renderTags"] = (
    value,
    getTagProps,
  ) =>
    value.map((option, index) => {
      const { key, ...tagProps } = getTagProps({ index });
      const tagLabel = option.inputValue || option.label;

      return (
        <Chip
          data-test={`tag-option-${tagLabel}`}
          variant="filled"
          label={tagLabel}
          key={`chip-${tagLabel}-${index}`}
          {...tagProps}
          onDelete={() => onDelete(tagLabel, !data?.includes(tagLabel))}
        />
      );
    });

  const renderInput: AutocompleteProps<ITagOption, true, true, true, "div">["renderInput"] = (params) => (
    <TextField
      sx={{ "& fieldset": { border: "none" } }}
      placeholder="type to add tags"
      {...params}
      onKeyDown={(event: any) => {
        if (event.key === "Backspace") {
          event.stopPropagation();
        }
      }}
    />
  );

  const isOptionEqualToValue: AutocompleteProps<
    ITagOption,
    true,
    true,
    true,
    "div"
  >["isOptionEqualToValue"] = (option, value) =>
    (option.inputValue || option.label) === (value.inputValue || value.label);

  const getOptionKey: AutocompleteProps<ITagOption, true, true, true, "div">["getOptionKey"] = (option) =>
    typeof option === "string" ? option : option.inputValue || option.label;

  return (
    <Autocomplete
      onChange={handleChange}
      filterOptions={filterOptions}
      value={valueOptions}
      selectOnFocus
      clearOnBlur
      loading={isLoading}
      handleHomeEndKeys
      id="tag-search-and-add-autocomplete"
      options={availableOptions}
      getOptionLabel={getOptionLabel}
      renderOption={renderOption}
      disabled={disabled}
      renderTags={renderTags}
      loadingText="Loading tags..."
      fullWidth
      size="medium"
      multiple
      freeSolo
      renderInput={renderInput}
      disableClearable
      isOptionEqualToValue={isOptionEqualToValue}
      getOptionKey={getOptionKey}
    />
  );
};

export default function CandidateTags({
  candidate,
  optimisticLoading,
}: {
  candidate: ICandidateProfile;
  optimisticLoading: boolean;
}) {
  const { mutateAsync: saveTagsAsync, isLoading: isSaving } =
    useEditCandidateProfileMutation(optimisticLoading);
  const { displayError, displaySuccess } = useNotification();
  const [tags, setTags] = useState<string[]>(candidate.tags ?? []);

  const selectTags = useCallback((tag: string[]) => {
    setTags((t) => [...t, ...tag.filter((c) => !t.includes(c))]);
  }, []);
  const onDelete = useCallback((tag: string, isNew: boolean) => {
    setTags((t) => t.filter((c) => c !== tag));
  }, []);

  const saveAllTags = async (newTags: string[]) => {
    await saveTagsAsync(
      {
        candidateId: candidate.id,
        tags: newTags,
        phoneNumber: null,
        emailAddress: null,
      },
      {
        onSettled: (data, error) => {
          if (error || (data && !data.success)) {
            if (error) {
              displayError("There was an error saving tags.");
            } else {
              displayError(data?.errorMessage ?? "There was an error saving tags.");
            }
          } else {
            displaySuccess("Tags successfully saved.");
          }
        },
      },
    );
  };

  return (
    <>
      <Stack direction="row" alignItems="center" sx={{ width: "100%" }}>
        <TagAutoComplete existingTags={tags} onSelect={selectTags} disabled={isSaving} onDelete={onDelete} />
        <Button
          data-test="candidate-tag-save-btn"
          sx={{ height: 30 }}
          variant="outlined"
          onClick={() => saveAllTags(tags)}
        >
          Save
        </Button>
      </Stack>
    </>
  );
}
